import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { SectionHead } from '@/components/redesign/bits'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchField } from '@/components/ui/search-field'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { cn } from '@/lib/cn'
import { formatDateTime, numberFmt } from '@/lib/format'
import {
  type Announcement,
  type AnnouncementSend,
  type AnnouncementSendFilter,
  useAnnouncementSends,
} from './api'

const SEND = {
  sent: { label: 'Sent', tone: 'ok' },
  queued: { label: 'Waiting', tone: 'warn' },
  skipped: { label: 'Skipped', tone: '' },
  failed: { label: 'Failed', tone: 'bad' },
} as const satisfies Record<AnnouncementSend['status'], { label: string; tone: string }>

const statuses: { value: AnnouncementSendFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'queued', label: 'Waiting' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'failed', label: 'Failed' },
]

/**
 * Every copy of every announcement, like Lifecycle emails' sends: who it went to, whether it
 * was sent, skipped (and why) or failed. With `announcementId`, only that announcement's copies.
 */
export function AnnouncementSends({
  announcements = [],
  announcementId,
}: {
  announcements?: Announcement[]
  announcementId?: string
}) {
  const [params, setParams] = useSearchParams()
  const update = (next: Record<string, string>, resetPage = false) =>
    setParams(
      (p) => {
        const merged = new URLSearchParams(p)
        for (const [k, v] of Object.entries(next)) {
          if (v === '') merged.delete(k)
          else merged.set(k, v)
        }
        if (resetPage) merged.delete('page')
        return merged
      },
      { replace: true },
    )
  const status = (params.get('sends') as AnnouncementSendFilter | null) ?? 'all'
  const announcement = announcementId ?? params.get('announcement') ?? ''
  const to = params.get('to') ?? ''
  const page = Number(params.get('page') ?? 1)
  const sends = useAnnouncementSends(status, announcement, to, page)
  const filtering = status !== 'all' || (announcementId === undefined && announcement !== '') || to !== ''

  const [draft, setDraft] = useState(to)
  useEffect(() => {
    const t = setTimeout(() => {
      if (draft.trim() !== to) update({ to: draft.trim() }, true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  return (
    <section className="sec">
      <SectionHead
        title="Email sends"
        extra={
          <div className="bar">
            <Segmented
              label="Send status"
              value={status}
              options={statuses}
              onChange={(v) => update({ sends: v === 'all' ? '' : v }, true)}
            />
            {announcementId === undefined && (
              <NativeSelect
                aria-label="Announcement"
                value={announcement}
                onChange={(e) => update({ announcement: e.target.value }, true)}
                className="h-9 w-52 text-[13px]"
              >
                <option value="">Every announcement</option>
                {announcements
                  .filter((a) => a.status !== 'draft')
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.subject}
                    </option>
                  ))}
              </NativeSelect>
            )}
            <SearchField
              label="Search sends"
              placeholder="Recipient’s email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
        }
      />

      {sends.isPending ? (
        <Skeleton className="h-40 rounded-[14px]" />
      ) : sends.isError ? (
        <div className="card">
          <ErrorState error={sends.error} onRetry={() => void sends.refetch()} />
        </div>
      ) : sends.data.data.length === 0 ? (
        <div className="card">
          <EmptyState
            title={filtering ? 'No sends match these filters' : 'Nothing sent yet'}
            description={
              filtering
                ? 'Try another status or announcement, or clear the search.'
                : 'Each person’s copy appears here once an announcement is sent.'
            }
            action={
              filtering && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setDraft('')
                    update({ sends: '', announcement: '', to: '' }, true)
                  }}
                >
                  Clear filters
                </button>
              )
            }
          />
        </div>
      ) : (
        <div className="tw">
          <table className="t" style={{ minWidth: 640 }}>
            <caption className="sr-only">Announcement sends</caption>
            <thead>
              <tr>
                {announcementId === undefined && <th>Announcement</th>}
                <th>To</th>
                <th>Status</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {sends.data.data.map((s) => (
                <tr key={s.id}>
                  {announcementId === undefined && (
                    <td>
                      <b>{s.announcement ?? 'Deleted announcement'}</b>
                    </td>
                  )}
                  <td>{s.to ?? <span className="m">—</span>}</td>
                  <td>
                    <span className={cn('chip', SEND[s.status].tone)}>{SEND[s.status].label}</span>
                    {s.reason && <small style={{ whiteSpace: 'normal' }}>{s.reason}</small>}
                  </td>
                  <td className="m" style={{ whiteSpace: 'nowrap' }}>
                    {s.sent_at
                      ? `Sent ${formatDateTime(s.sent_at)}`
                      : s.queued_at
                        ? `Queued ${formatDateTime(s.queued_at)}`
                        : '—'}
                    {s.clicked_at ? (
                      <small>Clicked {formatDateTime(s.clicked_at)}</small>
                    ) : (
                      s.opened_at && <small>Opened {formatDateTime(s.opened_at)}</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="t-foot">
            <span>
              {sends.data.meta.last_page > 1
                ? `Page ${sends.data.meta.current_page} of ${sends.data.meta.last_page} · `
                : ''}
              {numberFmt.format(sends.data.meta.total)} {sends.data.meta.total === 1 ? 'send' : 'sends'}
            </span>
            {sends.data.meta.last_page > 1 && (
              <span className="acts">
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page <= 1}
                  onClick={() => update({ page: String(page - 1) })}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page >= sends.data.meta.last_page}
                  onClick={() => update({ page: String(page + 1) })}
                >
                  Next
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
